import { normalizeDiscoverPrivacy, toDiscoverPrivacy } from './discoverVisibility';

const FALLBACK_CENTER = Object.freeze({ lat: 37.7749, lng: -122.4194 });
const FALLBACK_ZOOM = 3;
const FALLBACK_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const DEFAULT_BOUNDS_PRECISION = 4;
const LATITUDE_RANGE = Object.freeze({ min: -90, max: 90 });
const LONGITUDE_RANGE = Object.freeze({ min: -180, max: 180 });

const MARKER_TYPE_FALLBACK = 'POINT';
const MARKER_PRIVACY_FALLBACK = 'PUBLIC';
const MAX_DISPLAY_NAME_LENGTH = 64;

const BOOLEAN_TRUE_VALUES = new Set(['true', '1', 'yes', 'on']);
const BOOLEAN_FALSE_VALUES = new Set(['false', '0', 'no', 'off']);

function sanitizeDisplayString(value, maxLength = MAX_DISPLAY_NAME_LENGTH) {
    if (typeof value !== 'string') {
        return '';
    }
    const stripped = value
        .replace(/[\r\n\t]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    if (!stripped) {
        return '';
    }
    return stripped.slice(0, maxLength);
}

function looksLikeEmail(candidate) {
    return typeof candidate === 'string' && /.+@.+\..+/.test(candidate);
}

function looksLikePhone(candidate) {
    if (typeof candidate !== 'string') {
        return false;
    }
    const digits = candidate.replace(/[^\d]/g, '');
    return digits.length >= 7;
}

function resolveAuthorName(rawMarker, metadata) {
    const candidates = [
        rawMarker.authorName,
        rawMarker.createdByName,
        rawMarker.createdByDisplayName,
        rawMarker.createdByFullName,
        rawMarker.createdBy,
        rawMarker.created_by,
        rawMarker.ownerName,
        metadata?.createdByName,
        metadata?.createdByDisplayName,
        metadata?.authorName,
        metadata?.author,
    ];

    for (const candidate of candidates) {
        if (typeof candidate !== 'string') {
            continue;
        }
        const trimmed = candidate.trim();
        if (!trimmed) {
            continue;
        }
        const looksNumericId = /^\d+$/.test(trimmed);
        const looksUuidLike = /^[0-9a-f-]{16,}$/i.test(trimmed);
        if (looksNumericId || looksUuidLike || looksLikeEmail(trimmed) || looksLikePhone(trimmed)) {
            continue;
        }
        return sanitizeDisplayString(trimmed);
    }

    return '';
}

function sanitizeDate(value) {
    if (!value) {
        return null;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function sanitizeCoordinate(value, fallback = FALLBACK_CENTER.lat) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number.parseFloat(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return fallback;
}

function sanitizeBoundCoordinate(value, min, max) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value >= min && value <= max ? value : null;
    }

    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number.parseFloat(value);
        if (Number.isFinite(parsed) && parsed >= min && parsed <= max) {
            return parsed;
        }
    }

    return null;
}

function parseCoordinateNumber(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
}

function normalizeLongitude(value) {
    if (!Number.isFinite(value)) {
        return null;
    }

    const normalized = (((value + 180) % 360) + 360) % 360 - 180;
    if (normalized === -180 && value > 0) {
        return 180;
    }
    return normalized;
}

function clampZoom(value, min = 0, max = 18) {
    return Math.min(Math.max(value, min), max);
}

function sanitizeZoom(value, fallback = FALLBACK_ZOOM) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return clampZoom(value);
    }

    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number.parseInt(value, 10);
        if (Number.isFinite(parsed)) {
            return clampZoom(parsed);
        }
    }

    return fallback;
}

function sanitizeTileUrl(value) {
    if (typeof value !== 'string') {
        return '';
    }
    return value.trim();
}

function sanitizeAttribution(value, fallback = FALLBACK_ATTRIBUTION) {
    if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
    }
    return fallback;
}

function sanitizeCenter(candidate, fallback = FALLBACK_CENTER) {
    if (!candidate || typeof candidate !== 'object') {
        return fallback;
    }

    const lat = sanitizeCoordinate(candidate.lat ?? candidate.latitude, fallback.lat);
    const lng = sanitizeCoordinate(candidate.lng ?? candidate.lon ?? candidate.longitude, fallback.lng);

    return { lat, lng };
}

function sanitizeBounds(candidate) {
    if (!candidate || typeof candidate !== 'object') {
        return null;
    }

    const north = sanitizeBoundCoordinate(candidate.north, LATITUDE_RANGE.min, LATITUDE_RANGE.max);
    const south = sanitizeBoundCoordinate(candidate.south, LATITUDE_RANGE.min, LATITUDE_RANGE.max);
    const east = sanitizeBoundCoordinate(candidate.east, LONGITUDE_RANGE.min, LONGITUDE_RANGE.max);
    const west = sanitizeBoundCoordinate(candidate.west, LONGITUDE_RANGE.min, LONGITUDE_RANGE.max);

    if ([north, south, east, west].some((value) => value == null)) {
        return null;
    }

    if (north < south) {
        return null;
    }

    return {
        north,
        south,
        east,
        west,
    };
}

function normalizeWrappedBounds(candidate) {
    if (!candidate || typeof candidate !== 'object') {
        return null;
    }

    const north = sanitizeBoundCoordinate(candidate.north, LATITUDE_RANGE.min, LATITUDE_RANGE.max);
    const south = sanitizeBoundCoordinate(candidate.south, LATITUDE_RANGE.min, LATITUDE_RANGE.max);
    const rawEast = parseCoordinateNumber(candidate.east);
    const rawWest = parseCoordinateNumber(candidate.west);

    if ([north, south, rawEast, rawWest].some((value) => value == null)) {
        return null;
    }

    if (north < south) {
        return null;
    }

    const longitudinalSpan = Math.abs(rawEast - rawWest);
    if (!Number.isFinite(longitudinalSpan)) {
        return null;
    }

    if (longitudinalSpan >= 360) {
        return {
            north,
            south,
            east: LONGITUDE_RANGE.max,
            west: LONGITUDE_RANGE.min,
        };
    }

    const east = normalizeLongitude(rawEast);
    const west = normalizeLongitude(rawWest);

    if (east == null || west == null) {
        return null;
    }

    return {
        north,
        south,
        east,
        west,
    };
}

function roundToPrecision(value, decimals = DEFAULT_BOUNDS_PRECISION) {
    if (!Number.isFinite(value)) {
        return value;
    }
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
}

function serializeBoundsKey(bounds, decimals = DEFAULT_BOUNDS_PRECISION) {
    if (!bounds) {
        return 'all';
    }
    const north = roundToPrecision(bounds.north, decimals).toFixed(decimals);
    const south = roundToPrecision(bounds.south, decimals).toFixed(decimals);
    const east = roundToPrecision(bounds.east, decimals).toFixed(decimals);
    const west = roundToPrecision(bounds.west, decimals).toFixed(decimals);
    return `${north}|${south}|${east}|${west}`;
}

function isCoordinateWithinBounds(coordinate, bounds) {
    if (!bounds || !coordinate) {
        return false;
    }
    const lat = sanitizeBoundCoordinate(coordinate.lat ?? coordinate.latitude, LATITUDE_RANGE.min, LATITUDE_RANGE.max);
    const lng = sanitizeBoundCoordinate(coordinate.lng ?? coordinate.longitude ?? coordinate.lon, LONGITUDE_RANGE.min, LONGITUDE_RANGE.max);
    if (lat == null || lng == null) {
        return false;
    }
    if (lat < bounds.south || lat > bounds.north) {
        return false;
    }
    if (bounds.west <= bounds.east) {
        return lng >= bounds.west && lng <= bounds.east;
    }
    return lng >= bounds.west || lng <= bounds.east;
}

function sanitizeBoolean(value, fallback = true) {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim().toLowerCase();
        if (BOOLEAN_TRUE_VALUES.has(trimmed)) {
            return true;
        }
        if (BOOLEAN_FALSE_VALUES.has(trimmed)) {
            return false;
        }
    }

    if (typeof value === 'number') {
        if (Number.isNaN(value)) {
            return fallback;
        }
        return value !== 0;
    }

    return fallback;
}

function sanitizeMarker(rawMarker, options = {}) {
    if (!rawMarker || typeof rawMarker !== 'object') {
        return null;
    }
    const { privacyFallback = MARKER_PRIVACY_FALLBACK } = options;

    const rawId = rawMarker.id ?? rawMarker.identifier ?? rawMarker.name;

    if (typeof rawId !== 'string' || rawId.trim().length === 0) {
        return null;
    }

    const latitude = sanitizeCoordinate(
        rawMarker.coordinate?.latitude
            ?? rawMarker.coordinate?.lat
            ?? rawMarker.latitude
            ?? rawMarker.lat,
        Number.NaN
    );
    const longitude = sanitizeCoordinate(
        rawMarker.coordinate?.longitude
            ?? rawMarker.coordinate?.lng
            ?? rawMarker.coordinate?.lon
            ?? rawMarker.longitude
            ?? rawMarker.lng
            ?? rawMarker.lon,
        Number.NaN
    );

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return null;
    }

    const metadata = (rawMarker.metadata && typeof rawMarker.metadata === 'object' && !Array.isArray(rawMarker.metadata))
        ? Object.freeze({ ...rawMarker.metadata })
        : Object.freeze({});

    const authorName = resolveAuthorName(rawMarker, metadata);
    const createdByCandidate = rawMarker.createdBy
        ?? rawMarker.created_by
        ?? rawMarker.createdById
        ?? rawMarker.created_by_id
        ?? rawMarker.createdByUserId
        ?? rawMarker.createdByUser
        ?? rawMarker.createdByUsername
        ?? rawMarker.owner
        ?? rawMarker.ownerId
        ?? metadata.owner
        ?? metadata.ownerId;
    const createdById = typeof createdByCandidate === 'string' || typeof createdByCandidate === 'number'
        ? String(createdByCandidate).trim()
        : null;

    const createdAt = sanitizeDate(
        rawMarker.createdAt
            ?? rawMarker.created_at
            ?? rawMarker.createdOn
            ?? rawMarker.created_on
            ?? rawMarker.created
    );

    const type = typeof rawMarker.type === 'string' && rawMarker.type.trim()
        ? rawMarker.type.trim().toUpperCase()
        : MARKER_TYPE_FALLBACK;

    const privacy = rawMarker.privacy != null
        ? normalizeDiscoverPrivacy(rawMarker.privacy, privacyFallback)
        : toDiscoverPrivacy(rawMarker.visibility, privacyFallback);

    const sanitizedMarker = {
        id: rawId.trim(),
        name: typeof rawMarker.name === 'string' ? rawMarker.name.trim() : '',
        type,
        coordinate: Object.freeze({
            lat: latitude,
            lng: longitude,
        }),
        metadata,
        authorName,
        createdById,
        createdAt,
        privacy,
    };

    // Preserve popup overrides from custom layer annotation
    if (rawMarker._popupOverrides && typeof rawMarker._popupOverrides === 'object') {
        sanitizedMarker._popupOverrides = rawMarker._popupOverrides;
    }

    return Object.freeze(sanitizedMarker);
}

function sanitizeMarkers(markers, options = {}) {
    if (!Array.isArray(markers)) {
        return [];
    }

    const deduped = new Map();

    for (const marker of markers) {
        const sanitized = sanitizeMarker(marker, options);
        if (sanitized && !deduped.has(sanitized.id)) {
            deduped.set(sanitized.id, sanitized);
        }
    }

    return Array.from(deduped.values());
}

export {
    FALLBACK_ATTRIBUTION,
    FALLBACK_CENTER,
    FALLBACK_ZOOM,
    DEFAULT_BOUNDS_PRECISION,
    clampZoom,
    sanitizeAttribution,
    sanitizeBoolean,
    sanitizeBounds,
    normalizeWrappedBounds,
    sanitizeCenter,
    sanitizeCoordinate,
    sanitizeMarker,
    sanitizeMarkers,
    sanitizeTileUrl,
    sanitizeZoom,
    serializeBoundsKey,
    isCoordinateWithinBounds,
};
